/**
 * DocumentBanners — conditional alert/warning banners shown above the tab bar.
 *
 * Extracted from DocumentModal.tsx to reduce render complexity.
 * Purely presentational: all state is passed as props.
 *
 * Banners (in render order):
 * 1. Expense Source Banner — shows when an expense was created from a fleet expense
 * 2. Batched Invoice Lock Warning — strict workflow mode
 * 3. Risk Detection Banner — fraud/risk indicators from useRiskAssessment
 *
 * Note: The Missing Supplier Warning was moved to DocumentModal.tsx, directly
 * below the header, so it is always visible above the contact search and line
 * item sections.
 */

import React from 'react';
import { Truck, Camera, AlertTriangle, ShieldAlert } from 'lucide-react';
import { type DocumentFormState } from '../documentModalHelpers';
import { type DocumentType } from '@/hooks/useDocumentsWorkItems';
import {
  type RiskAssessmentResult,
  type RiskFlag,
} from '@/lib/finance/riskDetection';

export interface DocumentBannersProps {
  doc_type: DocumentType;
  formData: DocumentFormState;
  isDarkMode: boolean;
  isPurchaseDocument: boolean;
  hasDocumentContact: boolean;
  isBatchedInvoiceLocked: boolean;
  isCompactMobileDoc: boolean;
  riskAssessment: {
    assessment: RiskAssessmentResult | null;
    riskFlags: RiskFlag[];
    hasUnacknowledgedBlocking: boolean;
    acknowledgeAll: () => void;
  };
}

export function DocumentBanners({
  doc_type,
  formData,
  isDarkMode,
  isPurchaseDocument,
  hasDocumentContact,
  isBatchedInvoiceLocked,
  isCompactMobileDoc,
  riskAssessment,
}: DocumentBannersProps) {
  return (
    <>
      {/* Expense Source Banner - Shows when expense was created from fleet expense */}
      {doc_type === 'expense' && formData.custom_fields?.source === 'fleet_expense' && (
        <div className={`px-3 sm:px-6 py-3 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${isDarkMode
          ? 'bg-amber-900/30 border-amber-700/50 text-amber-200'
          : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-amber-800/50' : 'bg-amber-100'}`}>
              <Truck size={18} className={isDarkMode ? 'text-amber-400' : 'text-amber-600'} />
            </div>
            <div>
              <div className="font-medium text-sm">Created from Fleet Expense</div>
              <div className={`text-xs ${isDarkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
                {typeof formData.custom_fields?.expense_category === 'string' && (
                  <span className="capitalize">{(formData.custom_fields.expense_category as string).replace('_', ' ')}</span>
                )}
                {typeof formData.custom_fields?.vehicle_registration === 'string' && (
                  <span> • Vehicle: {formData.custom_fields.vehicle_registration as string}</span>
                )}
                {typeof formData.custom_fields?.source_expense_id === 'string' && (
                  <span> • Expense #{(formData.custom_fields.source_expense_id as string).slice(0, 8)}</span>
                )}
              </div>
            </div>
          </div>
          {typeof formData.custom_fields?.receipt_photo_url === 'string' && (
            <a
              href={formData.custom_fields.receipt_photo_url as string}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDarkMode
                ? 'bg-amber-800/50 hover:bg-amber-700/50 text-amber-200'
                : 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                }`}
            >
              <Camera size={14} />
              View Receipt Photo
            </a>
          )}
        </div>
      )}

      {/* Batched invoice lock warning (strict mode) */}
      {isBatchedInvoiceLocked && (
        <div className={`border-b px-3 py-2.5 sm:px-6 ${isDarkMode ? 'border-gray-700 bg-red-900/20' : 'border-red-200 bg-red-50'}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            <div className="min-w-0 flex-1">
              <p className={`text-xs font-semibold ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                Invoice locked — strict workflow mode
              </p>
              <p className={`text-xs mt-0.5 leading-5 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                This invoice is part of an approved batch and cannot be edited. To make changes, create a credit note.
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Risk Detection Banner */}
      {riskAssessment.assessment && riskAssessment.riskFlags.length > 0 && (
        <div className={`${isCompactMobileDoc ? 'px-3 py-2' : 'px-6 py-2.5'} ${riskAssessment.hasUnacknowledgedBlocking ? (isDarkMode ? 'bg-red-900/30 border-b border-red-800' : 'bg-red-50 border-b border-red-200') : (isDarkMode ? 'bg-amber-900/20 border-b border-amber-800' : 'bg-amber-50 border-b border-amber-200')}`}>
          <div className="flex items-start gap-2">
            <ShieldAlert className={`w-4 h-4 mt-0.5 shrink-0 ${riskAssessment.hasUnacknowledgedBlocking ? 'text-red-500' : 'text-amber-500'}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-semibold ${riskAssessment.hasUnacknowledgedBlocking ? (isDarkMode ? 'text-red-300' : 'text-red-700') : (isDarkMode ? 'text-amber-300' : 'text-amber-700')}`}>
                {riskAssessment.assessment.summary}
              </div>
              <div className={`text-[11px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {riskAssessment.riskFlags.map((f) => f.title).join(' • ')}
              </div>
            </div>
            {riskAssessment.hasUnacknowledgedBlocking && (
              <button
                type="button"
                onClick={() => riskAssessment.acknowledgeAll()}
                className={`text-[11px] font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-red-800 text-red-100 hover:bg-red-700' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
              >
                Acknowledge
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default DocumentBanners;
